/** @format */

import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom"; // Use Link for navigation
import { motion, AnimatePresence } from "framer-motion";
import {
	ShoppingCart,
	Tag,
	XCircle,
	Info,
	Lock,
	Trash2,
	Edit,
	Copy,
} from "lucide-react"; // Added more icons

// Actions & Components (Ensure paths are correct)
import {
	readPromo,
	removeFromCart,
	saveCartSummary,
	updateProductQuantity,
	addToCart,
	resetCart,
} from "../actions/cartActions";
import { customizeProduct } from "../actions/blindActions";
import { removeEmailFromCartMailChimp } from "../actions/userActions";
import { Toast } from "./customize/Toast"; // Assuming path is correct
import { CartItem } from "../components/CartItem"; // Assuming path is correct
import { CartSummary } from "../components/CartSummary"; // Assuming path is correct
import { calculateTotals } from "../utils/calulateTotals"; // Assuming path is correct
import { LoadingSpinner } from "../components/LoadingSpinner"; // Assuming exists

const CartScreen = () => {
	const navigate = useNavigate(); // Use navigate instead of history
	const dispatch = useDispatch();

	// --- Redux State ---
	const { cartItems = [], cartSummary = {} } = useSelector(
		(state) => state.cart
	);
	const { userInfo } = useSelector((state) => state.userLogin);
	const country = useSelector((state) => state.country); // Ensure correct path

	// --- Component State ---
	const [promoCode, setPromoCode] = useState(cartSummary?.promoCode || "");
	const [promoAmount, setPromoAmount] = useState(
		cartSummary?.withPromoPrice || 0
	); // Discount value applied
	const [isApplyingPromo, setIsApplyingPromo] = useState(false); // Loading state for promo apply
	const [promoError, setPromoError] = useState(""); // Error message specifically for promo code input
	const [toast, setToast] = useState(null); // General feedback { message, type }
	const [isCheckingOut, setIsCheckingOut] = useState(false); // Loading state for checkout process

	const userId = userInfo?.id;
	const userEmail = userInfo?.email;
	const history = useNavigate();

	// --- Memoized Calculations ---
	const totals = useMemo(() => {
		// Provide default structure if calculateTotals might fail or cart is empty
		try {
			return calculateTotals(cartItems, country, 0, promoAmount);
		} catch (e) {
			console.error("Error calculating totals:", e);
			return { items: [], msrp: 0, ourPrice: 0, savings: 0 };
		}
	}, [cartItems, country, promoAmount]);

	// --- Cart Management Handlers (Memoized) ---
	const handleQuantityChange = useCallback(
		(index, quantity) => {
			if (quantity >= 1) {
				// Allow setting quantity to 1 even if it was 1 already
				dispatch(updateProductQuantity(index, quantity));
				setToast(null); // Clear previous toasts on interaction
			} else {
				// Optionally show a toast if trying to go below 1
				setToast({
					type: "info",
					message: "Quantity must be at least 1. Use Remove instead.",
				});
			}
		},
		[dispatch]
	);

	const handleRemove = useCallback(
		(index, itemName) => {
			// Dispatch remove action
			dispatch(removeFromCart(index));
			// Provide user feedback
			setToast({
				type: "info",
				message: `"${itemName || "Item"}" removed from cart.`,
			});
			// Check if cart becomes empty after removal (Redux state updates asynchronously)
			// The MailChimp logic might be better handled server-side or based on the final cart state length check
			// if (cartItems.length - 1 === 0 && userEmail) {
			// 	removeEmailFromCartMailChimp(userEmail);
			// }
		},
		[dispatch, userEmail]
	); // Removed cartItems from dep array - relies on index

	const handleDuplicate = useCallback(
		(index) => {
			if (cartItems[index]) {
				dispatch(addToCart(cartItems[index])); // Dispatch duplicate action
				setToast({
					type: "success",
					message: `"${
						cartItems[index]?.productInfo?.OCS_Name || "Item"
					}" duplicated.`,
				});
			}
		},
		[dispatch, cartItems]
	);

	const handleEdit = useCallback(
		(index) => {
			const itemToEdit = cartItems[index];
			if (!itemToEdit) return;

			// Prepare item state for customization page
			const stateToDispatch = {
				...itemToEdit, // Spread the existing item
				optionInfo: {
					...itemToEdit.optionInfo,
					editId: index, // Add/Update the editId marker
				},
			};

			dispatch(customizeProduct(stateToDispatch)); // Dispatch the state to customization reducer

			// Determine navigation path based on whether it's a 'Customized' product
			const isCustom = itemToEdit.productInfo?.Program?.includes("Customized");
			const targetPath = isCustom
				? `/customize/details` // Assuming details is the first step
				: `/shop/${encodeURIComponent(
						itemToEdit.productInfo?.Type?.toLowerCase() || ""
				  )}/${encodeURIComponent(
						itemToEdit.productInfo?.New_Program?.replaceAll(" ", "_") || ""
				  )}`; // Fallback/Other product edit path

			// Use URLSearchParams to preserve any necessary query params if needed
			// const params = new URLSearchParams(); // Add params if required by target pages
			// navigate(`${targetPath}?${params.toString()}`);
			navigate(targetPath); // Navigate to the edit page
		},
		[dispatch, history, cartItems]
	); // navigate depends on history? Use useNavigate return value

	// --- Promo Code Handling (Memoized) ---
	const handlePromoApply = useCallback(async () => {
		if (!promoCode.trim()) {
			setPromoError("Please enter a promo code.");
			return;
		}
		setIsApplyingPromo(true);
		setPromoError(""); // Clear previous errors
		setToast(null);

		try {
			const res = await readPromo(promoCode, userId || -1); // Pass -1 for guest

			if (!res || !res.data || res.data.length === 0) {
				throw new Error("Invalid promo code."); // Handle case where API returns empty/invalid response
			}

			const promoData = res.data[0];
			const cartTotal = totals.ourPrice; // Use memoized total

			// --- Validation ---
			// Expired?
			if (promoData?.Expired) {
				throw new Error("This promo code has expired.");
			}
			// Referral code login check
			if (promoData?.Description === "referral" && !userId) {
				throw new Error("Please login to use a referral code.");
			}
			// Minimum purchase check
			if (
				(promoData?.Description === "New Promo" ||
					promoData?.Description === "wallpaper") &&
				cartTotal < 100
			) {
				// Use strict <
				throw new Error(
					`Minimum $100 purchase required for code "${promoCode}".`
				);
			}
			// Add other validation rules here if needed (e.g., one-time use, specific products)

			// --- Apply Promo ---
			const discountValue = promoData?.percentage
				? cartTotal * (parseFloat(promoData.percentage) / 100)
				: parseFloat(promoData.val) || 0;

			// Prevent applying negative discount or discount larger than total
			const appliedDiscount = Math.max(0, Math.min(discountValue, cartTotal));

			setPromoAmount(appliedDiscount); // Update the state holding the discount amount
			setToast({
				type: "success",
				message: `Promo code "${promoCode}" applied successfully!`,
			});
			// No need to return value, state update triggers recalculation via useMemo
		} catch (error) {
			console.error("Error applying promo:", error);
			setPromoError(error.message || "Invalid promo code."); // Display specific error message
			setPromoAmount(0); // Reset discount amount on error
		} finally {
			setIsApplyingPromo(false);
		}
	}, [promoCode, userId, totals.ourPrice, dispatch]); // Dependencies

	const handlePromoRemove = useCallback(() => {
		setPromoCode("");
		setPromoAmount(0);
		setPromoError("");
		setToast({ type: "info", message: "Promo code removed." });
		// Optionally save cleared promo state to Redux cartSummary if needed immediately
		// dispatch(saveCartSummary({ ...cartSummary, promoCode: "", withPromoPrice: 0 }));
	}, [dispatch, cartSummary]); // Include cartSummary if dispatching

	// --- Checkout Handler (Memoized) ---
	const handleCheckout = useCallback(
		async (e) => {
			e?.preventDefault(); // Prevent default if called from form submit
			if (cartItems.length === 0) {
				setToast({ type: "error", message: "Your cart is empty." });
				return;
			}

			setIsCheckingOut(true);
			setToast(null);

			try {
				// Recalculate final totals just before saving - using memoized `totals` is usually fine
				const finalTotals = totals; // Use the memoized value

				// Prepare summary data to save
				const summaryToSave = {
					FINAL_MSRP: finalTotals.msrp,
					FINAL_PRICE: finalTotals.ourPrice, // This is price *before* promo for subtotal calculation? Check logic
					SUBTOTAL: finalTotals.ourPrice - promoAmount, // Price *after* promo? Clarify definition
					SAVINGS: finalTotals.savings + promoAmount, // Total savings including promo
					FINAL_TAX: 0, // Tax calculated later?
					promoCode: promoAmount > 0 ? promoCode : "", // Only save code if discount applied
					withPromoPrice: promoAmount, // The actual discount amount applied
				};
				console.log("Saving Cart Summary:", summaryToSave);

				await dispatch(saveCartSummary(summaryToSave));

				// Navigate to the next step (e.g., shipping/placeorder)
				navigate("/placeorder", { state: { prevPath: "cart" } });
			} catch (error) {
				console.error("Error proceeding to checkout:", error);
				setToast({
					type: "error",
					message: `Checkout error: ${error.message || "Please try again."}`,
				});
				setIsCheckingOut(false); // Re-enable button on error
			}
			// No finally block needed if navigating away on success
		},
		[dispatch, navigate, cartItems, totals, promoCode, promoAmount]
	);

	// --- Effect for Mailchimp removal on empty cart ---
	useEffect(() => {
		// Check specifically if the cart *became* empty (e.g., prev length > 0, current == 0)
		// This basic check runs whenever cartItems changes and is empty
		if (cartItems.length === 0 && userEmail) {
			console.log(
				"Cart is empty, attempting Mailchimp removal for:",
				userEmail
			);
			removeEmailFromCartMailChimp(userEmail); // Fire and forget, okay if it fails silently?
		}
	}, [cartItems, userEmail]); // Rerun only if cartItems or userEmail changes

	// --- Render ---
	return (
		<div className="min-h-screen bg-gray-100 py-8 md:py-12">
			<div className="container mx-auto px-4 max-w-6xl">
				{" "}
				{/* Slightly smaller max-width */}
				{/* Header */}
				<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 md:mb-8 pb-4 border-b border-gray-200">
					<div className="flex items-center gap-3">
						<ShoppingCart className="w-7 h-7 text-primary" />
						<h1 className="text-2xl md:text-3xl font-bold text-gray-900">
							Your Shopping Cart
						</h1>
					</div>
					{cartItems.length > 0 && (
						<span className="text-sm text-gray-600">
							{cartItems.length} item(s)
						</span>
					)}
				</div>
				{/* --- Main Content --- */}
				<AnimatePresence mode="wait">
					{cartItems.length === 0 ? (
						// --- Empty Cart State ---
						<motion.div
							key="empty-cart"
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0 }}
							className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-12 text-center flex flex-col items-center"
						>
							<ShoppingCart
								className="w-16 h-16 text-gray-300 mb-5"
								strokeWidth={1}
							/>
							<h2 className="text-xl font-semibold text-gray-800 mb-2">
								Your Cart is Empty
							</h2>
							<p className="text-gray-500 mb-6 max-w-sm">
								Looks like you haven't added any custom shades yet. Explore our
								collections to find the perfect fit!
							</p>
							<Link
								to="/shop" // Link to main shop page
								className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-white font-semibold rounded-lg shadow-sm hover:bg-primary/90 transition-colors text-base"
							>
								Continue Shopping
							</Link>
						</motion.div>
					) : (
						// --- Cart Items + Summary Grid ---
						<motion.div
							key="cart-content"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							className="grid grid-cols-1 lg:grid-cols-[2fr,1fr] gap-6 md:gap-8 items-start"
						>
							{/* Left: Cart Items */}
							<div className="space-y-4">
								<AnimatePresence initial={false}>
									{totals.items.map((item, index) => (
										<motion.div
											key={item.cartItemId || index} // Use a stable unique ID if available from `calculateTotals`
											layout // Animate layout changes (add/remove)
											initial={{ opacity: 0, height: 0 }}
											animate={{ opacity: 1, height: "auto" }}
											exit={{
												opacity: 0,
												height: 0,
												marginBottom: 0,
												marginTop: 0,
											}} // Prevent margin jump on exit
											transition={{ duration: 0.3, ease: "easeInOut" }}
											className="origin-top" // Helps with height animation
										>
											<CartItem
												item={item}
												index={index} // Pass index if needed by handlers
												country={country} // Pass country for potential display needs
												onQuantityChange={handleQuantityChange}
												onRemove={handleRemove}
												onEdit={handleEdit}
												onDuplicate={handleDuplicate}
											/>
										</motion.div>
									))}
								</AnimatePresence>
							</div>

							{/* Right: Summary (Sticky on Desktop) */}
							<div className="lg:sticky lg:top-6 space-y-6">
								<CartSummary
									totals={totals} // Pass calculated totals
									promoCode={promoCode}
									onPromoChange={(e) => {
										setPromoCode(e.target.value);
										setPromoError("");
									}} // Clear error on change
									onPromoApply={handlePromoApply}
									onPromoRemove={handlePromoRemove} // Add remove handler
									promoError={promoError}
									isApplyingPromo={isApplyingPromo} // Pass loading state
									promoAmount={promoAmount} // Pass applied discount amount
									onCheckout={handleCheckout}
									isCheckingOut={isCheckingOut} // Pass loading state
								/>
								{/* Trust Badges */}
								<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center text-xs text-gray-500 space-y-1">
									<p className="flex items-center justify-center gap-1">
										<Lock size={14} /> Secure Checkout Process
									</p>
									<p>
										Questions?{" "}
										<Link
											to="/contact"
											className="text-primary hover:underline"
										>
											Contact Us
										</Link>
									</p>
								</div>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</div>

			{/* --- Toast Notifications --- */}
			<AnimatePresence>
				{toast && (
					<Toast
						show={!!toast}
						type={toast.type}
						message={toast.message}
						onClose={() => setToast(null)}
						duration={3000}
					/>
				)}
			</AnimatePresence>
		</div>
	);
};

export default CartScreen;
